import { useRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Calendar, Shield, BookOpen, Camera, Loader2, Check, Trash2 } from 'lucide-react'
import { apiService, getMediaUrl } from '@/services/api'
import { ChangePasswordDialog } from './ChangePasswordDialog'
import { AppearanceSection } from './AppearanceSection'
import { ImageCropDialog } from './ImageCropDialog'
import { toast } from 'sonner'
import type { User } from '@/types'

const MAX_AVATAR_SIZE = 10 * 1024 * 1024
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

// Tela de perfil (redesign 2026-07, mobile-first): header poster + grid
// 1fr/400px — dados da conta e seguranca a esquerda, Aparencia a direita.
export function ProfileScreen() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [showPasswordDialog, setShowPasswordDialog] = useState(false)
  const navigate = useNavigate()

  // form "Dados da conta" (inline, conforme o handoff)
  const [fullName, setFullName] = useState('')
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [saving, setSaving] = useState(false)

  // fluxo de avatar (upload + crop direto na tela)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null)
  const [avatarBusy, setAvatarBusy] = useState(false)

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const userData = await apiService.getCurrentUser()
        setUser(userData)
        setFullName(userData.full_name)
        setUsername(userData.username)
        setEmail(userData.email)
        setLoading(false)
      } catch (error) {
        console.error('Erro ao carregar o perfil do usuário:', error)
        navigate('/login')
      }
    }

    fetchUserProfile()
  }, [navigate])

  const getInitials = (name: string) =>
    name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2)

  const isDirty = user !== null && (
    fullName !== user.full_name || username !== user.username || email !== user.email
  )

  const resetForm = () => {
    if (!user) return
    setFullName(user.full_name)
    setUsername(user.username)
    setEmail(user.email)
  }

  const handleSaveProfile = async () => {
    if (!user) return
    if (!fullName.trim()) { toast.error('Informe seu nome completo'); return }
    if (!username.trim()) { toast.error('Informe seu nome de usuário'); return }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('Informe um email válido'); return }

    setSaving(true)
    try {
      const updatedUser = await apiService.updateCurrentUser({
        full_name: fullName.trim(),
        username: username.trim(),
        email: email.trim(),
      })
      setUser(updatedUser)
      localStorage.setItem('user', JSON.stringify(updatedUser))
      toast.success('Perfil atualizado com sucesso!')
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao atualizar perfil')
    } finally {
      setSaving(false)
    }
  }

  const handleChangePassword = async (currentPassword: string, newPassword: string) => {
    try {
      await apiService.changePassword(currentPassword, newPassword)
      toast.success('Senha alterada com sucesso!')
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao alterar senha')
      throw error // o dialog precisa saber que falhou
    }
  }

  const handleAvatarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Tipo de arquivo não permitido. Use JPG, PNG, WebP ou GIF.')
      return
    }
    if (file.size > MAX_AVATAR_SIZE) {
      toast.error('Arquivo muito grande. Máximo permitido: 10MB')
      return
    }
    const reader = new FileReader()
    reader.onload = (ev) => setCropImageSrc(ev.target?.result as string)
    reader.readAsDataURL(file)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleCropComplete = async (croppedFile: File) => {
    setCropImageSrc(null)
    setAvatarBusy(true)
    try {
      const { avatar_url } = await apiService.uploadAvatar(croppedFile)
      setUser(prev => prev ? { ...prev, avatar_url } : prev)
      toast.success('Foto atualizada!')
    } catch (error) {
      toast.error('Erro ao enviar a foto')
    } finally {
      setAvatarBusy(false)
    }
  }

  const handleRemoveAvatar = async () => {
    if (!user?.avatar_url || avatarBusy) return
    setAvatarBusy(true)
    try {
      await apiService.deleteAvatar()
      setUser(prev => prev ? { ...prev, avatar_url: undefined } : prev)
      toast.success('Foto removida')
    } catch (error) {
      toast.error('Erro ao remover a foto')
    } finally {
      setAvatarBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!user) return null

  const roleBadge = user.role === 'superadmin'
    ? { label: 'Admin', icon: Shield, className: 'bg-admin text-white' }
    : user.role === 'professor'
    ? { label: 'Professor', icon: Shield, className: '' }
    : { label: 'Estudante', icon: BookOpen, className: '' }
  const RoleIcon = roleBadge.icon

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex items-start gap-3 sm:gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="mt-1 shrink-0">
          <ArrowLeft className="h-5 w-5" />
        </Button>

        <div className="flex min-w-0 flex-1 flex-col gap-4 sm:flex-row sm:items-center">
          {/* Avatar redondo (preferencia do Paulo sobre o quadrado do handoff),
              com troca de foto */}
          <div className="relative shrink-0">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-primary text-2xl font-black text-primary-foreground sm:h-24 sm:w-24">
              {user.avatar_url
                ? <img src={getMediaUrl(user.avatar_url)} alt="Foto de perfil" className="h-full w-full object-cover" />
                : getInitials(user.full_name || user.username)}
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={avatarBusy}
              className="absolute -bottom-1.5 -right-1.5 flex h-8 w-8 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:text-primary"
              title="Alterar foto"
            >
              {avatarBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_TYPES.join(',')}
              onChange={handleAvatarFileChange}
              className="hidden"
            />
          </div>

          <div className="min-w-0">
            <h1 className="truncate text-2xl font-black uppercase italic leading-tight tracking-[-0.035em] sm:text-3xl">
              {user.full_name || user.username}
            </h1>
            <p className="truncate text-sm text-muted-foreground">{user.email}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge
                variant={user.role === 'estudante' ? 'secondary' : 'default'}
                className={roleBadge.className}
              >
                <RoleIcon className="mr-1 h-3 w-3" />
                {roleBadge.label}
              </Badge>
              <Badge variant="outline">
                <Calendar className="mr-1 h-3 w-3" />
                Desde {new Date(user.created_at || Date.now()).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })}
              </Badge>
              {user.avatar_url && (
                <button
                  onClick={handleRemoveAvatar}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-error"
                  disabled={avatarBusy}
                >
                  <Trash2 className="h-3 w-3" /> Remover foto
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Grid principal */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_400px]">
        {/* Coluna esquerda */}
        <div className="space-y-6">
          {/* Dados da conta */}
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle className="text-base font-black uppercase tracking-tight">Dados da conta</CardTitle>
              <CardDescription>Como você aparece na plataforma</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="profile-name">Nome completo</Label>
                <Input id="profile-name" value={fullName} onChange={e => setFullName(e.target.value)} autoComplete="name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="profile-username">Nome de usuário</Label>
                <Input id="profile-username" value={username} onChange={e => setUsername(e.target.value)} autoComplete="username" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="profile-email">Email</Label>
                <Input id="profile-email" type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" inputMode="email" />
              </div>
              <div className="flex gap-2 pt-1">
                <Button onClick={handleSaveProfile} disabled={!isDirty || saving} className="font-bold uppercase tracking-wide">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                  Salvar
                </Button>
                <Button variant="outline" onClick={resetForm} disabled={!isDirty || saving} className="font-bold uppercase tracking-wide">
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Segurança */}
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle className="text-base font-black uppercase tracking-tight">Segurança</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-section p-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold">Senha</p>
                  <p className="text-xs text-muted-foreground">
                    {user.auth_provider === 'google' ? 'Conta criada via Google' : '••••••••'}
                  </p>
                </div>
                {user.auth_provider !== 'google' && (
                  <Button variant="outline" size="sm" onClick={() => setShowPasswordDialog(true)} className="shrink-0 font-bold uppercase tracking-wide">
                    Alterar
                  </Button>
                )}
              </div>
              <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-section p-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold">Conta Google</p>
                  <p className="text-xs text-muted-foreground">Login com sua conta do Google</p>
                </div>
                <Badge className={user.auth_provider === 'google' ? 'bg-success-bg text-success border-none' : 'bg-section text-muted-foreground border-none'}>
                  {user.auth_provider === 'google' ? 'Conectada' : 'Não conectada'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Papel & permissões */}
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle className="text-base font-black uppercase tracking-tight">Papel &amp; permissões</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-3 rounded-lg border border-border bg-section p-3">
                <RoleIcon className={`mt-0.5 h-5 w-5 shrink-0 ${user.role === 'superadmin' ? 'text-admin' : 'text-primary'}`} />
                <div>
                  <p className="text-sm font-semibold">{roleBadge.label}</p>
                  <p className="text-xs text-muted-foreground">
                    {user.role === 'superadmin'
                      ? 'Acesso total: gerencia usuários, campanhas e o sistema.'
                      : user.role === 'professor'
                      ? 'Cria e gerencia campanhas, aulas e o livro interativo.'
                      : 'Participa de campanhas, cria personagens e joga o livro interativo.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Coluna direita: Aparência (seletor de tema por conta) */}
        <div className="space-y-6">
          <AppearanceSection />
        </div>
      </div>

      {/* Dialogs */}
      <ChangePasswordDialog
        open={showPasswordDialog}
        onOpenChange={setShowPasswordDialog}
        onSave={handleChangePassword}
      />

      {cropImageSrc && (
        <ImageCropDialog
          open={!!cropImageSrc}
          onOpenChange={(open) => { if (!open) setCropImageSrc(null) }}
          imageSrc={cropImageSrc}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  )
}
